import { useCallback } from "react";

import { Box, IconButton, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { generatePath, useNavigate } from "react-router";

import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useHubSlug } from "shared/hooks/useHubSlug";
import { useScan } from "shared/hooks/useScan/useScan";
import { PlusCircleIcon } from "ui/Icons/Icons";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import {
  BatteryLoading,
  BatteryLoadingError,
  NoBatteryInventory,
  BatteriesList,
  ScanBatteryCodeModal,
  IsNewBatteryModal,
} from "../components";
import { useBarcodeInformation } from "../hooks/useBarcodeInformation";
import { useGetAssetsByHubQuery, useSearchAssetsLazyQuery } from "../queries/assets.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import {
  ASSET_TYPE,
  FULL_LIST_ASSET_STATES,
  FUNCTIONAL_ASSET_STATES,
  NON_FUNCTIONAL_ASSET_STATES,
  VALID_REPORT_STATES,
} from "../utils/constants";

export function BatteryManagementListPage() {
  const intl = useIntl();
  const hubSlug = useHubSlug();
  const navigate = useNavigate();
  const {
    scannedBarcode,
    setScannedBarcode,
    setSearchedAsset,
    setMatchedSuppliersFromScan,
    setShouldUpdateAsset,
    setShouldCreateAsset,
    setShouldTransferAsset,
  } = useAssetManagementStore();
  const { showToastUI } = useCustomToast();
  const { processBarcode } = useBarcodeInformation();
  const [searchAssets, { loading: isSearchingAssets }] = useSearchAssetsLazyQuery({
    fetchPolicy: "network-only",
    onError: (error) => {
      showToastUI({
        status: "error",
        description: error.message,
      });
    },
  });

  const {
    isOpen: isOpenScanBatteryCodeModal,
    onClose: closeScanBatteryCodeModal,
    onToggle: toggleScanBatteryCodeModal,
  } = useDisclosure();

  const {
    isOpen: isOpenNewBatteryModal,
    onClose: closeNewBatteryModal,
    onOpen: openNewBatteryModal,
  } = useDisclosure();

  const {
    loading: loadingAllBatteries,
    error: errorAllBatteries,
    data: allBatteriesData,
  } = useGetAssetsByHubQuery({
    variables: {
      input: {
        state: FULL_LIST_ASSET_STATES,
        assetType: ASSET_TYPE,
      },
    },
    fetchPolicy: "network-only",
  });

  const currentHubList = allBatteriesData?.getAssetsByHub?.assets || [];
  const totalBatteryCount = currentHubList.length;

  const functionalBatteries = currentHubList.filter((battery) =>
    FUNCTIONAL_ASSET_STATES.includes(battery.state),
  );
  const nonFunctionalBatteries = currentHubList.filter((battery) =>
    NON_FUNCTIONAL_ASSET_STATES.includes(battery.state),
  );

  const title = intl.formatMessage({ id: "pages.inventory.battery-management-page.title" });
  const displayTitle = totalBatteryCount > 0 ? `${title} (${totalBatteryCount})` : title;

  const handleCreateNewBattery = useCallback(() => {
    const barcode = scannedBarcode;
    setShouldCreateAsset(true);
    if (isNotNullNorUndefined(barcode)) {
      navigate(generatePath(routes.inventory.batteryManagement.create, { serialNumber: barcode }));
    }
  }, [scannedBarcode, setShouldCreateAsset, navigate]);

  const handleScan = async (barcode: string) => {
    const { isValid, supplierName, matchedSuppliers, serialNumber } = processBarcode(barcode);
    if (!isValid) {
      showToastUI({
        id: "invalid_barcode_format",
        title: "pages.inventory.battery-management-page.invalid-barcode-format",
      });
      return;
    }

    // Valid barcode → call backend
    setScannedBarcode(serialNumber);
    setMatchedSuppliersFromScan(matchedSuppliers);

    const input = {
      assetType: ASSET_TYPE,
      serialNumber,
      hubCode: hubSlug,
      supplierId: supplierName,
    };
    const { data, error: errorSearchingAssets } = await searchAssets({ variables: { input } });
    const foundAssets = data?.searchAssets?.assets || [];

    const searchedAsset = foundAssets.find((asset) => asset.serialNumber === serialNumber);
    const assetInCurrentList = currentHubList.find((asset) => asset.serialNumber === serialNumber);

    const isSameHub = searchedAsset?.hubCode === hubSlug;
    const isOperationalOrDefect =
      assetInCurrentList && VALID_REPORT_STATES.includes(assetInCurrentList.state);

    setSearchedAsset(searchedAsset || null);

    if (searchedAsset) {
      if (isSameHub && isOperationalOrDefect) {
        navigate(
          generatePath(routes.inventory.batteryManagement.report, { batteryId: searchedAsset.id }),
        );
      } else if (isSameHub && !isOperationalOrDefect) {
        setShouldUpdateAsset(true);
        navigate(generatePath(routes.inventory.batteryManagement.create, { serialNumber }));
      } else if (!isSameHub) {
        setShouldTransferAsset(true);
        navigate(generatePath(routes.inventory.batteryManagement.create, { serialNumber }));
      }
    } else if (!searchedAsset && !errorSearchingAssets) {
      openNewBatteryModal();
    }
  };

  useScan({
    onScan: handleScan,
  });

  return (
    <Page isCentered isFull isBgGrey justifyContent="flex-start">
      <NavigationHeader
        title={displayTitle}
        onClickGoBack={() => navigate(routes.inventory.root)}
        rightElement={
          <IconButton
            onClick={toggleScanBatteryCodeModal}
            aria-label="Add Battery"
            bg="white"
            icon={<PlusCircleIcon boxSize={6} />}
          />
        }
      />

      <Box position="relative" w="full" h="full" py="s200" bg="grey.150" overflow="auto">
        {(loadingAllBatteries || isSearchingAssets) && <BatteryLoading />}
        {errorAllBatteries && <BatteryLoadingError />}
        {!errorAllBatteries && totalBatteryCount === 0 && <NoBatteryInventory />}
        {!errorAllBatteries && totalBatteryCount > 0 && (
          <BatteriesList
            functionalList={functionalBatteries}
            nonFunctionalList={nonFunctionalBatteries}
          />
        )}
      </Box>

      <IsNewBatteryModal
        isOpen={isOpenNewBatteryModal}
        onClose={closeNewBatteryModal}
        onClickPrimary={handleCreateNewBattery}
        onClickSecondary={closeNewBatteryModal}
      />

      <ScanBatteryCodeModal
        isOpen={isOpenScanBatteryCodeModal}
        onClose={closeScanBatteryCodeModal}
        onClickSecondary={toggleScanBatteryCodeModal}
      />
    </Page>
  );
}
